/** The greeting itself, and the optional "there" / "noticeboard" that can follow it. */
const GREETING_START =
  "(?:hi+|hey+|hiya|hello+|howdy|yo|sup|g'?day|(?:good )?(?:morning|afternoon|evening)|good day|mornin'?|morning all)(?: there| noticeboard| nb| again| everyone)?";

/** "Hi", "hello there", "good morning": a greeting and nothing else. */
const GREETING = new RegExp(`^${GREETING_START}$`);

/**
 * A greeting said in front of the real message, with or without a comma:
 * "hi there what can you do?".
 */
const LEADING_GREETING = new RegExp(`^${GREETING_START}\\s+`);

/** "Thanks", "thank you so much", "cheers", "thanks that's the setup bit". */
const THANKS =
  /^(?:ok(?:ay)? )?(thanks?( you)?|ty|thx|cheers|ta|much appreciated)( so much| very much| a lot)?(,? (?:that'?s|thats|that is) [a-z' ]+| i(?:'ll| will) (?:ask|publish) [a-z' ]+)?$/;

/** Words that only set up a reaction: "really that's amazing". */
const REACTION_FILLER =
  "(?:oh|ah|wow|whoa|really|that'?s|thats|so|very|quite|pretty|super|just|yeah|yes|absolutely|honestly)";

/** "amazing", "that's great", "really thats amazing": praise and nothing else. */
const REACTION = new RegExp(
  `^(?:${REACTION_FILLER}\\s+)*(?:amazing|awesome|brilliant|wonderful|fantastic|excellent|impressive|lovely|perfect|great|good|nice|helpful|incredible|cool|unbelievable|wow|whoa)(?:\\s+(?:${REACTION_FILLER}|amazing|awesome|brilliant|wonderful|fantastic|excellent|impressive|lovely|perfect|great|good|nice|helpful|incredible|cool|unbelievable|wow|whoa|one))*(?:\\s+thanks?)?$`
);

export const GREETING_REPLY =
  "Hi! Ask me about Noticeboard, like \"Where can I see my Notices?\", or type a page name to jump there.";

export const THANKS_REPLY = "You're welcome! Anything else I can help with?";

export const REACTION_REPLY = "Glad you think so. What would you like to do?";

/**
 * "wow you are really bad at this", "you seem confused", "this is a bad experience":
 * frustration with the chat, not a question about Noticeboard.
 */
const FRUSTRATED =
  /^(?:(?:gosh|ugh|oh|wow)\s+)?(?:you(?:'re| are|r)|this is|that is) (?:a )?(?:really |so |very )?(?:bad|useless|terrible|awful|rubbish|unhelpful|frustrating)(?: experience| at this)?(?: are you not an ai)?$|^you seem (?:really |so |very )?(?:confused|lost)$|^(?:you )?(?:really )?(?:don't|dont|do not) know how to (?:speak|talk) to me$|^(?:i )?(?:don't|dont|do not) know what you(?:'re| are) talking about$|^what are you talking about$|^that means nothing to me$|^are you not an ai$|^gosh$|^hmm+$|^this is (?:risiulous|ridiculou?s|a joke)$|^i(?:'?m| am) (?:so |really )?confused$/;

/** "not really, I was just saying hi": they were greeting, and the last reply missed it. */
const JUST_SAYING_HI =
  /^(?:(?:no|nope|not really|sorry) )?(?:i was |i'm |im |i am )?(?:only |just )?saying (?:hi|hello|hey)$/;

export const FRUSTRATED_REPLY = "Sorry. Tell me what you were trying to do and I'll help with just that.";

/** "why", "what", "how" on their own: not a question yet, and not a page or video title. */
const BARE_QUESTION = /^(?:why|what|whats|huh|how|who|when|where|which)$/;

export const BARE_QUESTION_REPLY = "Ask me a bit more, like what's on this week, or how to send a notice.";

/** "ok", "nvm", "sorry": the message is finished. Not a new question. */
const ACK =
  /^(?:ok|okay|k|sorry|no problem|no worries|that'?s all|cheers|ta|(?:i think )?i can find it now|i(?:'ll| will) start there|i(?:'ll| will) do (?:those|that|it) myself|if they still can'?t see it i(?:'ll| will) (?:phone|call|ring) them)$/;

export const ACK_REPLY = "You're welcome. Anything else?";

/** "okay bye", "ok thanks bye": leaving, so a goodbye rather than "you're welcome". */
const GOODBYE =
  /^(?:(?:ok|okay|k|right|thanks|thank you|cheers|ta|that'?s all|that is all|all done|i'?m done)\s+)*(?:bye|bye bye|goodbye|good bye|goodnight|good night|see you(?: later| soon| tomorrow)?|see ya(?: later)?|cya|later|catch you later|talk (?:to you )?later|ttyl)$/;

/** "how are you today?", "how's it going": asked of Ask itself. */
const HOW_ARE_YOU =
  /^(?:and\s+)?(?:how are you(?: doing)?(?: today)?|how'?s it going|how are things|you good|are you well|are you (?:ok|okay|alright|good)(?: today)?|you (?:ok|okay|alright)(?: today)?|how'?s your day(?: going)?)$/;
export const HOW_ARE_YOU_REPLY = "I'm well, thanks for asking! What can I help you with on Noticeboard?";

/** "what's your name?", "who are you?", "are you a real person?", "who made you?" */
const ABOUT_ASK =
  /^(?:what'?s|what is) your name$|^are you (?:a )?(?:real )?(?:person|human|robot|bot|ai|real)$|^who (?:made|built|created|programmed|designed) (?:you|this|it|this (?:chat|bot|assistant|app))$|^are you real$|^(?:am i|are we) (?:talking|chatting|speaking) (?:to|with) (?:a )?(?:real )?(?:robot|bot|person|human|ai|machine|computer)$|^is (?:this|it) (?:a )?(?:real )?(?:human|person|robot|bot|ai|machine)$/;
export const ABOUT_ASK_REPLY =
  "I'm Ask Noticeboard, the assistant built into this dashboard. I'm not a person, but I can find things, answer questions about Noticeboard, and get a Notice, Poll or Event ready for you to check.";

/** "I'm so nervous, it's my first week teaching": a new Team member, a little worried. */
const NERVOUS = /^(?:i'?m|i am|feeling|it'?s my)\b.*\b(?:nervous|scared|anxious|overwhelmed|worried|first (?:day|week))\b/;
/** "sorry I'm new and a bit lost", "first week here, be gentle with me": the whole message, with no question in it. */
const NEW_HERE =
  /^(?:sorry )?(?:i'?m|im|i am) (?:new|brand new|still new|a bit lost|lost)(?: here)?(?: and (?:a bit |very |so )?(?:lost|confused|nervous))?$|^(?:it'?s )?(?:my )?first (?:day|week)(?: here)?(?: be gentle with me| go easy on me| so be gentle| please be patient)?$/;
export const NERVOUS_REPLY =
  "Welcome, you'll be fine! A good start is Noticeboard Fundamentals in the Help Center. Or ask me to make your first Notice: I'll get it ready, and nothing is sent until you check it and press Publish.";

export const GOODBYE_REPLY = "Bye! Ask me again any time.";

/** True when the whole message is one question word, such as "why". */
export const isBareQuestionWord = (text: string) => BARE_QUESTION.test(normalise(text));

/** Lower case, letters and apostrophes only, single-spaced: for matching a whole short message. */
export const normalise = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z' ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** "Hi" or "good morning", as normalised: also said before a question ("hi, what can you do?") */
export const isGreeting = (said: string) => GREETING.test(said);

/** Drops a greeting from the front of an already-normalised message: "hi there what can you do" → "what can you do". */
export const stripLeadingGreeting = (said: string) => said.replace(LEADING_GREETING, "");

/** The reply to a message that's only a greeting, a thank-you, or a reaction, which needs no matching. */
export function smallTalkReply(text: string): string | null {
  const said = normalise(text);
  if (isGreeting(said) || JUST_SAYING_HI.test(said)) return GREETING_REPLY;
  if (THANKS.test(said)) return THANKS_REPLY;
  if (REACTION.test(said)) return REACTION_REPLY;
  if (FRUSTRATED.test(said)) return FRUSTRATED_REPLY;
  if (GOODBYE.test(said)) return GOODBYE_REPLY;
  if (HOW_ARE_YOU.test(said)) return HOW_ARE_YOU_REPLY;
  if (ABOUT_ASK.test(said)) return ABOUT_ASK_REPLY;
  if ((NERVOUS.test(said) && said.split(" ").length <= 14) || NEW_HERE.test(said)) return NERVOUS_REPLY;
  if (ACK.test(said)) return ACK_REPLY;
  if (isBareQuestionWord(said)) return BARE_QUESTION_REPLY;
  return null;
}
